import React from 'react';

import { motion } from 'framer-motion';

import { useDynamicBackground } from 'hooks/useDynamicBackground';
import { useDynamicLogoForm } from 'hooks/useDynamicLogoForm';
import { gradientClasses, animationVariants } from 'lib/design-tokens';
import { cn } from 'lib/utils';

/**
 * PublicLayout - Minimal layout for unauthenticated pages
 *
 * This layout is designed for public pages (Login, Register, ForgotPassword, etc.)
 * and only includes whitelabel features without any authentication-dependent hooks.
 *
 * Features:
 * - Theme colors (provided by ThemeProvider above)
 * - Dynamic logo via useDynamicLogoForm
 * - Dynamic background via useDynamicBackground
 * - Decorative gradient blurs
 * - Responsive design (mobile + desktop)
 *
 * Does NOT include:
 * - useMeetingRooms
 * - useMenu or MenuContext
 * - Any hooks that call authenticated endpoints
 */

interface PublicLayoutProps {
  children: React.ReactNode;
  /** Center content vertically and horizontally (default: true) */
  centered?: boolean;
  /** Show decorative blur elements (default: true) */
  showDecorativeBlurs?: boolean;
  /** Custom className for the container */
  className?: string;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  centered = true,
  showDecorativeBlurs = true,
  className,
}) => {
  // Whitelabel hooks only - no authentication required
  const { mediaUrl: logoUrl, isLoading: logoLoading } = useDynamicLogoForm();
  const { mediaUrl: backgroundUrl, isLoading: backgroundLoading } = useDynamicBackground();

  return (
    <div
      className={cn(
        'min-h-screen w-full',
        centered && 'flex items-center justify-center',
        !backgroundUrl && gradientClasses.primary,
        'relative overflow-hidden',
        className
      )}
      style={
        backgroundUrl
          ? {
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {/* Decorative background blurs */}
      {showDecorativeBlurs && (
        <>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="absolute top-10 left-10 w-48 h-48 sm:w-72 sm:h-72 bg-primary/30 rounded-full blur-3xl"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="absolute bottom-10 right-10 w-48 h-48 sm:w-72 sm:h-72 bg-primary/20 rounded-full blur-3xl"
            aria-hidden="true"
          />
        </>
      )}

      {/* Main content wrapper */}
      <motion.div
        initial={animationVariants.fadeIn.initial}
        animate={animationVariants.fadeIn.animate}
        transition={animationVariants.fadeIn.transition}
        className="relative z-10 w-full h-full"
      >
        {children}
      </motion.div>
    </div>
  );
};

/**
 * PublicLayoutLogo - Standalone logo component for use within PublicLayout
 *
 * Can be used inside cards/forms to display the whitelabel logo.
 * Uses the same useDynamicLogoForm hook for consistency.
 */
interface PublicLayoutLogoProps {
  className?: string;
  /** Custom height class (default: 'h-16 sm:h-20') */
  heightClass?: string;
}

export const PublicLayoutLogo: React.FC<PublicLayoutLogoProps> = ({
  className,
  heightClass = 'h-16 sm:h-20',
}) => {
  const { mediaUrl: logoUrl, isLoading } = useDynamicLogoForm();

  if (isLoading || !logoUrl) {
    return null;
  }

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.4 }}
      className={cn('flex justify-center', className)}
    >
      <img src={logoUrl} alt="Logo" className={cn(heightClass, 'object-contain')} loading="eager" />
    </motion.div>
  );
};

export default PublicLayout;
